import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Users, Eye, Edit3, Clock, Activity } from 'lucide-react';

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 px-3 py-2">
      <Icon size={16} className="text-slate-500" />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</div>
      </div>
    </div>
  );
}

/**
 * Reusable "Usage" panel — drop into any asset detail page.
 * Props:
 *   objectType: string — one of report/dashboard/insight/sentinel/dataset/...
 *   objectId:   string — the asset's ID
 *   title:      string (optional) — heading override
 */
export default function AssetUsageTab({ objectType, objectId, title }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!objectType || !objectId) return;
    let alive = true;
    setLoading(true);
    api.getAssetUsage(objectType, objectId)
      .then(s => { if (alive) { setStats(s); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e.message || 'Failed to load stats'); setLoading(false); } });
    return () => { alive = false; };
  }, [objectType, objectId]);

  if (loading) {
    return <div className="p-4 text-sm text-slate-500" data-testid="asset-usage-loading">Loading usage…</div>;
  }
  if (err) {
    return <div className="p-4 text-sm text-red-600" data-testid="asset-usage-error">{err}</div>;
  }
  if (!stats) return null;

  return (
    <div data-testid="asset-usage-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title || 'Usage'}</h3>
        <span className="text-[11px] text-slate-500">Last 60 days</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Eye} label="Views" value={stats.view_count || 0} />
        <Kpi icon={Edit3} label="Edits" value={stats.edit_count || 0} />
        <Kpi icon={Users} label="Unique viewers" value={stats.unique_viewer_count || 0} />
        <Kpi icon={Clock} label="Last accessed" value={stats.last_accessed_at ? new Date(stats.last_accessed_at).toLocaleDateString() : '—'} />
      </div>

      {(stats.top_viewers || []).length > 0 && (
        <div>
          <div className="text-xs uppercase text-slate-500 mb-2">Top viewers</div>
          <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {(stats.top_viewers || []).map((v, i) => (
                  <tr key={v.user_id || i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{v.user_email || v.user_id || '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{v.view_count} view{v.view_count === 1 ? '' : 's'}</td>
                    <td className="px-3 py-2 text-right text-slate-400 text-xs">{new Date(v.last_viewed_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-2"><Activity size={12} /> Recent activity</div>
        {(stats.recent_activity || []).length === 0 ? (
          <div className="text-sm text-slate-400 italic">No activity recorded yet.</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {(stats.recent_activity || []).slice(0, 12).map((e, i) => (
              <li key={i} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 py-1">
                <span className="text-slate-700 dark:text-slate-300">
                  <span className="inline-block text-[10px] uppercase font-semibold text-slate-500 mr-2">{e.event_type}</span>
                  {e.user_email || e.user_id || '—'}
                </span>
                <span className="text-xs text-slate-400">{new Date(e.timestamp).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
